import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { Wallet } from "lucide-react";

/**
 * Customer Portal — Wallet view (customer sees only their own account).
 * Consumes GET /portal/finance/wallet (scoped by JWT to the logged-in
 * customer_account_id). Available-facility is hidden by default (tenant
 * must opt-in via customer_portal_facility_visible flag).
 */
export default function PortalWallet() {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/portal/finance/wallet")
      .then(({ data }) => setWallet(data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  if (error) {
    return <WidgetError error={error} onRetry={load}
                        title="Could not load wallet" testId="portal-wallet-error"/>;
  }
  if (loading || !wallet) {
    return <div className="label-overline text-slate-500" data-testid="portal-wallet-loading">Loading wallet…</div>;
  }

  const { balance_cents, credit_limit_cents, available_facility_cents,
          overdue_cents, unallocated_credit_cents, aging } = wallet;
  const facilityShown = available_facility_cents !== undefined;

  return (
    <div data-testid="portal-wallet-page">
      <div className="flex items-center gap-2 mb-4">
        <Wallet size={20}/>
        <h1 className="text-3xl font-black tracking-tight">My Wallet</h1>
      </div>
      <p className="text-sm text-slate-600 mb-6">
        This is your live account with this truck stop. All values come directly from their finance ledger.
      </p>

      <div className={"grid gap-3 mb-6 " + (facilityShown ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-2")}>
        <Metric testId="portal-wallet-balance" label="Amount you owe"
                value={fmtMoney(Math.max(balance_cents, 0))}
                tone={balance_cents > 0 ? "warn" : "good"}/>
        <Metric testId="portal-wallet-overdue" label="Overdue"
                value={fmtMoney(overdue_cents)}
                tone={overdue_cents > 0 ? "danger" : "neutral"}/>
        {facilityShown && (
          <>
            <Metric testId="portal-wallet-credit-limit" label="Credit limit"
                    value={credit_limit_cents == null ? "—" : fmtMoney(credit_limit_cents)}/>
            <Metric testId="portal-wallet-available-facility" label="Available facility"
                    value={available_facility_cents == null ? "—" : fmtMoney(available_facility_cents)}
                    tone={available_facility_cents != null && available_facility_cents <= 0 ? "danger" : "good"}/>
          </>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-5 mb-6">
        <div className="label-overline mb-3">Aging (past due days)</div>
        <div className="grid grid-cols-5 gap-2 text-center">
          <Bucket label="Current" value={aging.current}/>
          <Bucket label="1–30"    value={aging.d30}/>
          <Bucket label="31–60"   value={aging.d60}/>
          <Bucket label="61–90"   value={aging.d90}/>
          <Bucket label="90+"     value={aging.d120plus} danger/>
        </div>
      </div>

      {unallocated_credit_cents > 0 && (
        <div className="border border-blue-200 bg-blue-50 rounded p-3 text-sm text-blue-800"
             data-testid="portal-wallet-unallocated-credit">
          You have <b className="font-mono">{fmtMoney(unallocated_credit_cents)}</b> in credit — it will
          be applied to your next invoice.
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone = "neutral", testId }) {
  const tones = {
    neutral: "text-slate-900 border-slate-200",
    warn: "text-amber-800 border-amber-300 bg-amber-50",
    danger: "text-red-800 border-red-300 bg-red-50",
    good: "text-green-800 border-green-300 bg-green-50",
  };
  return (
    <div className={"border rounded p-3 " + tones[tone]} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl leading-tight">{value}</div>
    </div>
  );
}

function Bucket({ label, value, danger = false }) {
  return (
    <div className={"border rounded p-2 " +
      (value > 0 && danger ? "border-red-300 bg-red-50" :
       value > 0 ? "border-amber-300 bg-amber-50" :
       "border-slate-200")}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-xs font-bold">{fmtMoney(value)}</div>
    </div>
  );
}
